class StickyAddToCart {
  constructor() {
    this.originalButton = document.querySelector('#add-to-cart');
    this.stickyButton = document.querySelector('.sticky-add-to-cart');
    this.mainQuantityInput = document.querySelector('#product-container .quantity-input');
    this.stickyQuantityInput = document.querySelector('.sticky-add-to-cart .quantity-input');
    this.stickyIncrementButton = document.querySelector('.sticky-add-to-cart .increment-button');
    this.stickyDecrementButton = document.querySelector('.sticky-add-to-cart .decrement-button');
    this.variantError = this.stickyButton.querySelector('.variant-error');
    this.stickyVariantWarning = this.stickyButton.querySelector('.sticky-variant-warning');
    this.stickyQuantityForm = this.stickyButton.querySelector('.quantity-form');
    this.stickyForm = document.getElementById('sticky-add-to-cart');

    if (!this.originalButton || !this.stickyButton) return;

    this.setupIntersectionObserver();
    this.setupQuantityControls();
    this.syncButtonStates(); // Initial sync
    this.setupStickyFormHandler();
  }

  setupStickyFormHandler() {
    const stickyButton = this.stickyButton.querySelector('[data-add-to-cart-button]');
    if (stickyButton) {
      stickyButton.addEventListener('click', (e) => {
        e.preventDefault();
        // Scroll to the main add-to-cart button instead of submitting the form
        this.scrollToMainATC();
        return false;
      });
    }
  }

  scrollToMainATC() {
    if (this.originalButton) {
      this.originalButton.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  showVariantWarning() {
    if (this.variantError) {
      this.variantError.classList.remove('hidden');
      this.variantError.classList.add('flex');
      setTimeout(() => {
        this.variantError.classList.remove('flex');
        this.variantError.classList.add('hidden');
      }, 2000);
    }
  }

  showStickyVariantWarning() {
    if (this.stickyVariantWarning) {
      this.stickyVariantWarning.classList.remove('hidden');
      setTimeout(() => {
        this.stickyVariantWarning.classList.add('hidden');
      }, 2000);
    }
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only show sticky bar if ATC is out of view AND user has scrolled past it
          const atcRect = this.originalButton.getBoundingClientRect();
          const atcOutOfView = entry.intersectionRatio < 1;
          const atcAboveViewport = atcRect.top < 0;
          if (atcOutOfView && atcAboveViewport) {
            this.stickyButton.classList.remove('hidden');
          } else {
            this.stickyButton.classList.add('hidden');
          }
        });
      },
      {
        threshold: 1.0, // Only fully visible counts as visible
        rootMargin: '0px 0px 0px 0px',
      }
    );

    this.observer.observe(this.originalButton);
  }

  syncButtonStates() {
    // Main buttons (not inside sticky bar)
    const mainATC = document.querySelector(
      '[data-add-to-cart-button]:not(.sticky-add-to-cart [data-add-to-cart-button])'
    );
    const mainOOS = document.querySelector(
      '[data-out-of-stock-button]:not(.sticky-add-to-cart [data-out-of-stock-button])'
    );
    // Sticky buttons
    const stickyATC = this.stickyButton.querySelector('[data-add-to-cart-button]');
    const stickyOOS = this.stickyButton.querySelector('[data-out-of-stock-button]');

    if (mainATC && stickyATC) {
      stickyATC.className = mainATC.className;
      stickyATC.disabled = mainATC.disabled;
    }
    if (mainOOS && stickyOOS) {
      stickyOOS.className = mainOOS.className;
      stickyOOS.disabled = mainOOS.disabled;
    }
  }

  setupQuantityControls() {
    if (!this.stickyQuantityInput || !this.mainQuantityInput) return;

    // Polling interval to always sync sticky with main
    let lastMainValue = this.mainQuantityInput.value;
    setInterval(() => {
      if (this.mainQuantityInput.value !== lastMainValue) {
        lastMainValue = this.mainQuantityInput.value;
        this.stickyQuantityInput.value = lastMainValue;
        this.syncButtonStates();
      }
    }, 100);

    // Show warning if user tries to interact with disabled controls
    const showWarningIfDisabled = (e) => {
      if (e.target.disabled || this.stickyQuantityInput.disabled) {
        e.preventDefault();
        this.showVariantWarning();
      }
    };
    if (this.stickyQuantityInput) {
      this.stickyQuantityInput.addEventListener('click', showWarningIfDisabled);
      this.stickyQuantityInput.addEventListener('focus', showWarningIfDisabled);
    }
    if (this.stickyIncrementButton) {
      this.stickyIncrementButton.addEventListener('click', showWarningIfDisabled);
    }
    if (this.stickyDecrementButton) {
      this.stickyDecrementButton.addEventListener('click', showWarningIfDisabled);
    }

    // Show sticky-variant-warning when quantity form is clicked and no variant is selected
    if (this.stickyQuantityForm) {
      this.stickyQuantityForm.addEventListener('click', (e) => {
        if (this.stickyQuantityInput.disabled) {
          e.preventDefault();
          this.showStickyVariantWarning();
        }
      });
    }

    // Sync main when sticky changes
    if (this.stickyIncrementButton) {
      this.stickyIncrementButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.stickyIncrementButton.disabled) return;

        const currentValue = parseInt(this.stickyQuantityInput.value) || 1;
        const newValue = currentValue + 1;
        this.stickyQuantityInput.value = newValue;
        this.mainQuantityInput.value = newValue;

        // Trigger the main quantity change event
        const event = new Event('change', { bubbles: true });
        this.mainQuantityInput.dispatchEvent(event);
        this.syncButtonStates();
      });
    }

    if (this.stickyDecrementButton) {
      this.stickyDecrementButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.stickyDecrementButton.disabled) return;

        const currentValue = parseInt(this.stickyQuantityInput.value) || 1;
        if (currentValue > 1) {
          const newValue = currentValue - 1;
          this.stickyQuantityInput.value = newValue;
          this.mainQuantityInput.value = newValue;

          // Trigger the main quantity change event
          const event = new Event('change', { bubbles: true });
          this.mainQuantityInput.dispatchEvent(event);
          this.syncButtonStates();
        }
      });
    }

    // Sync initial state
    this.stickyQuantityInput.value = this.mainQuantityInput.value;
    this.syncButtonStates();
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new StickyAddToCart();
});
